import { Grid, Card, Typography, Tooltip, Box } from '@mui/material';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { useParams, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import InitialDueDiligenceForm from './InitialDueDiligenceForm';
import { INITIAL_DUE_DILIGENCE } from '../formQuestionsEnum';
import {
  getDefaultFacility,
  getDefaultPortfolio,
  getStatusConfig,
  getInitialDueDiligenceData,
  getUserPermissions,
  getCompanyOptionsList,
  getFiftyStates,
  getFacilityStructures,
} from '../../../../redux/slices/transitions';
import FacilityActions from './FacilityActions';
import UpdateStatusButtons from '../UpdateStatusButtons';
import SkeletonBlock from '../SkeletonBlock';
import AccessDenied from '../../../../reusable-components/accessDenied/AccessDenied';
import { selectedTabStyle, cardStyle } from '../TransitionStyles';
import InitialDueDiligenceSaveButton from './InitialDueDiligenceSaveButton';
import CanceledAlert from '../CanceledAlert';
import FormsDropdown from '../FormsDropdown';
import BackToHomeButton from '../BackToHomeButton';
import { PAGE_HEIGHT } from '../constants';

export const defaultFacTab = {
  facilityName: 'New Facility',
  sellerFacilityId: 0,
  isViewable: true,
};

export default function InitialDueDiligenceView() {
  const dispatch = useDispatch();
  const [portfolio, setPortfolio] = useState({ portfolioFields: [], facilityFields: [], facilityTabs: [] });
  const {
    data: {
      defaultFacility,
      defaultPortfolio,
      statusConfig,
      userPermissions,
      companyOptions,
      fiftyStates,
      facilityStructures,
    },
  } = useSelector((state) => state.transitions);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [isLoadingForms, setIsLoadingForms] = useState(false);
  const [isAddingFac, setIsAddingFac] = useState(false);
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get('facilityId');

  useEffect(() => {
    if (defaultPortfolio == null) dispatch(getDefaultPortfolio());
    if (defaultFacility == null) dispatch(getDefaultFacility());
    if (statusConfig == null) dispatch(getStatusConfig());
    if (!userPermissions[id] && id) dispatch(getUserPermissions(id));
    if (companyOptions.length === 0) dispatch(getCompanyOptionsList());
    if (fiftyStates.length === 0) dispatch(getFiftyStates());
    if (facilityStructures.length === 0) dispatch(getFacilityStructures());

    if (String(facilityId) === String(0)) {
      searchParams.delete('facilityId');
      setSearchParams(searchParams);
    }
  }, []);

  useEffect(() => {
    if (defaultFacility != null && defaultPortfolio != null) {
      setIsLoadingForms(true);

      if (!id) {
        setPortfolio({
          portfolioFields: defaultPortfolio,
          facilityFields: defaultFacility,
          isAdmin: true,
          facilityTabs: [{ ...defaultFacTab, sellerFacilityId: null }],
        });
        setIsLoadingForms(false);
        setIsLoadingPage(false);
        return;
      }

      if (String(facilityId) === String(0)) {
        setPortfolio((prev) => ({ ...prev, facilityFields: defaultFacility, facilityTabs: [...prev.facilityTabs] }));
        setIsLoadingForms(false);
        setIsLoadingPage(false);
        return;
      }

      dispatch(getInitialDueDiligenceData(id, facilityId))
        .then((resp) => {
          const facTabs = isAddingFac ? [...resp.facilityTabs, defaultFacTab] : [...resp.facilityTabs];
          setPortfolio({
            ...resp,
            facilityTabs: facTabs,
          });
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setIsLoadingForms(false);
          setIsLoadingPage(false);
        });
    }
  }, [defaultFacility, defaultPortfolio, id, facilityId]);

  const formData = useMemo(() => {
    const { facilityFields, portfolioFields } = portfolio;

    const fieldNameMap = Object.fromEntries(Object.entries(INITIAL_DUE_DILIGENCE).map(([, v]) => [v.id, v.fieldName]));

    const mappedPortfolioFields = portfolioFields?.reduce(
      (acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }),
      {}
    );

    const mappedFacilityFields = {
      ...facilityFields?.reduce((acc, field) => ({ ...acc, [fieldNameMap[field.formQuestionId]]: field.answer }), {}),
    };

    return {
      ...mappedPortfolioFields,
      ...mappedFacilityFields,
    };
  }, [portfolio]);

  const methods = useForm({ defaultValues: formData, shouldUnregister: false });
  const { reset } = methods;

  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      reset(formData);
    }
  }, [formData]);

  const tabIndex = useMemo(() => {
    let idx = 0;

    if (facilityId && portfolio?.facilityTabs) {
      idx = portfolio.facilityTabs?.findIndex((fac) => String(fac?.sellerFacilityId) === String(facilityId));
    }

    return idx !== -1 ? idx : 0;
  }, [portfolio]);

  const handleTabChange = (index) => {
    if (index === tabIndex) return;
    const facId = portfolio?.facilityTabs[index]?.sellerFacilityId;
    if (facId !== null) {
      searchParams.set('facilityId', facId);
      setSearchParams(searchParams);
    }
  };

  const tabDisabledMessage = (facIdx) => {
    if (isAddingFac) return 'Save or delete the new facility before switching tabs';
    if (tabIndex !== facIdx && methods.formState.isDirty) return 'Save changes before changing tabs';
    return null;
  };

  return (
    <>
      <FormProvider {...methods}>
        <form>
          {isLoadingPage ? (
            <Grid container spacing={2}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={2}>
                <SkeletonBlock height={240} />
                <SkeletonBlock height={90} sx={{ mt: 2 }} />
              </Grid>

              {/* MAIN CONTENT */}
              <Grid item xs={12} md={8}>
                <SkeletonBlock height={60} />
                <SkeletonBlock height={520} sx={{ mt: 2 }} />
              </Grid>

              {/* RIGHT PANEL */}
              <Grid item xs={12} md={2}>
                <SkeletonBlock height={80} />
              </Grid>
            </Grid>
          ) : (
            <>
              <BackToHomeButton isAddingFac={isAddingFac} />
              <Grid sx={selectedTabStyle}>
                <Tabs selectedIndex={tabIndex} onSelect={handleTabChange}>
                  <Grid container spacing={2} sx={{ height: PAGE_HEIGHT }}>
                    <Grid item xs={12} sm={12} md={2}>
                      <TabList style={{ borderBottom: '0' }}>
                        {portfolio?.portfolioFields && (
                          <Card sx={cardStyle}>
                            <Typography variant="h6" color="info.main" sx={{ wordBreak: 'break-word' }}>
                              {formData?.portfolioName || 'New Portfolio'}
                            </Typography>
                            {portfolio?.facilityTabs.map((facility, facIdx) => {
                              const disabledMessage = tabDisabledMessage(facIdx);
                              return (
                                <Tab
                                  key={facility?.sellerFacilityId}
                                  style={{ border: 0, width: '100%', wordBreak: 'break-word' }}
                                  disabled={!!disabledMessage}
                                >
                                  <Tooltip
                                    placement="right"
                                    arrow
                                    title={disabledMessage}
                                    disableHoverListener={!disabledMessage}
                                  >
                                    <span>{facility?.facilityName}</span>
                                  </Tooltip>
                                </Tab>
                              );
                            })}

                            <FacilityActions
                              isAdmin={portfolio.isAdmin}
                              setPortfolio={setPortfolio}
                              isAddingFac={isAddingFac}
                              setIsAddingFacility={setIsAddingFac}
                            />
                          </Card>
                        )}
                        <Card sx={cardStyle}> Collaborator </Card>
                      </TabList>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={8}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ flexShrink: 0 }}>
                        <CanceledAlert />
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <FormsDropdown formName="Initial Due Diligence" isAddingFac={isAddingFac} />
                      </Box>
                      <Box
                        className="item-scrollbar"
                        sx={{
                          flexGrow: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                        }}
                      >
                        {portfolio?.facilityTabs?.map((facility, facIdx) => (
                          <TabPanel key={facIdx}>
                            {String(facility.sellerFacilityId) === String(facilityId) && (
                              <>
                                {isLoadingForms ? (
                                  <SkeletonBlock height={600} sx={{ mt: 2 }} />
                                ) : facility?.isViewable ? (
                                  <Card sx={{ ...cardStyle, py: 3, px: { xs: 4, sm: 8 } }}>
                                    <InitialDueDiligenceForm portfolio={portfolio} idx={facIdx} />
                                  </Card>
                                ) : (
                                  <AccessDenied />
                                )}
                              </>
                            )}
                          </TabPanel>
                        ))}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={12} md={2}>
                      <Card sx={cardStyle}>
                        <Grid container direction="column">
                          <Grid item>
                            <InitialDueDiligenceSaveButton
                              isAdmin={portfolio.isAdmin}
                              setPortfolio={setPortfolio}
                              setIsAddingFacility={setIsAddingFac}
                            />
                          </Grid>

                          <Grid item>
                            <UpdateStatusButtons
                              data={portfolio}
                              setData={setPortfolio}
                              isAddingFac={isAddingFac}
                              proceedButtonLabel="Proceed to Due Diligence"
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                </Tabs>
              </Grid>
            </>
          )}
        </form>
      </FormProvider>
    </>
  );
}
